// Mail sidebar — the folder rail of the /mail page (architecture/MAIL.md
// §3.1). Extends the shared AppSidebar shell so the chrome (width, glass
// card, scroll column) is identical to the chat + calendar rails, and nulls
// the same slots ChatSidebar nulls.
//
// The rail body is ONE definition (MailSidebarBody): the desktop rail, the
// phone sheet (MailMobileRail) and the docked drawer all render it, so every
// surface lists the same accounts + folders. Folder rows are STATIC keys —
// their onClick binds normally; only the DYNAMIC account/label rows (minted
// through childExtends + a children factory) ride `data-mail-*` delegation
// (pages/index.js), the v3.14 childExtends/onClick trap.
import { unreadForMailAccount } from '../functions/mailUtils.js'

// A folder row — the house SidebarRow with its "…" menu dropped and a fixed
// square icon slot so every row's label starts at the same x.
export const MailFolderRow = {
  extends: 'SidebarRow',
  MoreBtn: null,
  Icon: { boxSize: 'A', minWidth: 'A' }
}

// Folder rows ride the SAME `data-mail-folder` delegation as the dynamic
// label rows — one door means the page's handler also mirrors the URL and
// closes the phone rail for every row alike (a static onClick here skipped
// both, measured in the headless probe).
const _folderRow = (folder, iconName, labelKey) => ({
  extends: 'MailFolderRow',
  subscribeTo: ['mailActiveFolder'],
  cursor: 'pointer',
  attr: { 'data-mail-folder': folder, role: 'button', tabindex: '0' },
  background: (el, s) => (s.root && s.root.mailActiveFolder === folder ? 'hover' : 'transparent'),
  Icon: { name: iconName },
  Label: {
    subscribeTo: ['mailActiveFolder'],
    text: `{{ ${labelKey} | polyglot }}`,
    fontWeight: (el, s) => (s.root && s.root.mailActiveFolder === folder ? '700' : '500')
  },
  // The inbox row carries the unread count pill (brand hue — the chat
  // UnreadBadge idiom; 'blue' is a static token by design).
  UnreadBadge:
    folder === 'inbox'
      ? {
          subscribeTo: ['mailUnreadCounts', 'mailActiveAccountId'],
          hide: (el, s) => !unreadForMailAccount(s.root, s.root && s.root.mailActiveAccountId),
          tag: 'span',
          padding: 'V Z',
          round: 'radiusPill',
          fontSize: 'Y1',
          fontWeight: '700',
          background: 'blue',
          color: 'fillInk',
          marginLeft: 'auto',
          text: (el, s) => {
            const n = unreadForMailAccount(s.root, s.root && s.root.mailActiveAccountId)
            return n > 99 ? '99+' : String(n)
          }
        }
      : null
})

// ── accounts ("All inboxes" + one row per accessible account) ───────────────
export const MailAccountRow = {
  extends: 'MailFolderRow',
  attr: { 'data-mail-account': (el, s) => (s && s.id) || '', role: 'button', tabindex: '0' },
  background: (el, s) =>
    s.root && s.root.mailActiveAccountId === (s && s.id) ? 'hover' : 'transparent',
  Icon: { name: 'email' },
  Label: {
    text: (el, s) => (s && (s.displayName || s.address)) || '',
    fontWeight: (el, s) => (s.root && s.root.mailActiveAccountId === (s && s.id) ? '700' : '500')
  },
  // Needs-attention badge (§3.1 states table): reauth_required / error rows.
  AttentionDot: {
    hide: (el, s) => {
      const st = (s && s.status) || ''
      return !(st === 'reauth_required' || st === 'error')
    },
    boxSize: 'W W',
    round: 'circle',
    flexShrink: '0',
    marginLeft: 'auto',
    background: 'red',
    attr: { 'aria-label': (el) => el.call('polyglot', 'mail.sidebar.needsAttention') }
  }
}

export const MailAccountGroup = {
  extends: 'AppSidebarGroup',
  subscribeTo: ['mailAccounts'],
  hide: (el, s) => (((s.root && s.root.mailAccounts) || []).length || 0) < 2,
  label: (el) => el.call('polyglot', 'mail.sidebar.accounts'),
  List: {
    flow: 'y',
    gap: 'V',

    AllInboxes: {
      extends: 'MailFolderRow',
      subscribeTo: ['mailActiveAccountId'],
      cursor: 'pointer',
      // `all` is the sentinel — the delegated handler maps it to null.
      attr: { 'data-mail-account': 'all', role: 'button', tabindex: '0' },
      background: (el, s) => (s.root && s.root.mailActiveAccountId ? 'transparent' : 'hover'),
      Icon: { name: 'folder' },
      Label: {
        subscribeTo: ['mailActiveAccountId'],
        text: '{{ mail.sidebar.allInboxes | polyglot }}',
        fontWeight: (el, s) => (s.root && s.root.mailActiveAccountId ? '500' : '700')
      }
    },

    Rows: {
      flow: 'y',
      gap: 'V',
      childrenAs: 'state',
      childExtends: 'MailAccountRow',
      childrenDeps: [
        (el, s) => s.root && s.root.mailAccounts,
        (el, s) => s.root && s.root.mailActiveAccountId
      ],
      children: (el, s) =>
        ((s.root && s.root.mailAccounts) || [])
          .filter((a) => a && a.status !== 'disabled')
          .map((a) => ({ ...a }))
    }
  }
}

// ── folders ─────────────────────────────────────────────────────────────────
export const MailFolderGroup = {
  extends: 'AppSidebarGroup',
  label: (el) => el.call('polyglot', 'mail.sidebar.folders'),
  List: {
    flow: 'y',
    gap: 'V',
    InboxRow: _folderRow('inbox', 'email', 'mail.folder.inbox'),
    StarredRow: _folderRow('starred', 'star', 'mail.folder.starred'),
    SentRow: _folderRow('sent', 'send', 'mail.folder.sent'),
    DraftsRow: _folderRow('drafts', 'edit', 'mail.folder.drafts'),
    ArchiveRow: _folderRow('archive', 'archive', 'mail.folder.archive'),
    SpamRow: _folderRow('spam', 'warning', 'mail.folder.spam'),
    TrashRow: _folderRow('trash', 'trash', 'mail.folder.trash')
  }
}

// ── labels (the ACTIVE account's provider labels) ───────────────────────────
export const MailLabelRow = {
  extends: 'MailFolderRow',
  attr: {
    'data-mail-folder': (el, s) => `label:${(s && s.id) || ''}`,
    role: 'button',
    tabindex: '0'
  },
  background: (el, s) =>
    s.root && s.root.mailActiveFolder === `label:${(s && s.id) || ''}` ? 'hover' : 'transparent',
  Icon: { name: 'label' },
  Label: { text: (el, s) => (s && s.name) || '' }
}

const _labelFolders = (s) => {
  const id = s.root && s.root.mailActiveAccountId
  if (!id) return []
  const account = ((s.root && s.root.mailAccounts) || []).find((a) => a && a.id === id)
  return ((account && account.folders) || []).filter((f) => f && !f.role && f.id && f.name)
}

export const MailLabelGroup = {
  extends: 'AppSidebarGroup',
  subscribeTo: ['mailAccounts', 'mailActiveAccountId'],
  hide: (el, s) => !_labelFolders(s).length,
  label: (el) => el.call('polyglot', 'mail.sidebar.labels'),
  List: {
    flow: 'y',
    gap: 'V',
    childrenAs: 'state',
    childExtends: 'MailLabelRow',
    childrenDeps: [
      (el, s) => s.root && s.root.mailAccounts,
      (el, s) => s.root && s.root.mailActiveAccountId,
      (el, s) => s.root && s.root.mailActiveFolder
    ],
    children: (el, s) => _labelFolders(s).map((f) => ({ ...f }))
  }
}

// ── the shared rail body ────────────────────────────────────────────────────
// Compose above the groups, one scroll column. The Compose button is the
// screen's one brand-hue carrier (§4.6) and calls THE §3.2a entry point.
export const MailSidebarBody = {
  flow: 'y',
  gap: 'B',
  minWidth: '0',

  ComposeButton: {
    extends: 'Button',
    theme: 'primary',
    flexShrink: '0',
    alignSelf: 'start',
    Icon: { name: 'edit' },
    text: '{{ mail.compose.new | polyglot }}',
    onClick: (e, el) => {
      e.stopPropagation()
      el.call('openMailCompose')
    }
  },

  MailAccountGroup: {},
  MailFolderGroup: {},
  MailLabelGroup: {}
}

export const MailSidebar = {
  extends: 'AppSidebar',
  tag: 'nav',
  'aria-label': (el) => el.call('polyglot', 'mail.sidebar.folders'),
  PeopleSection: null,
  ViewSwitcher: null,
  Top: null,
  position: 'relative',
  minWidth: 'F3',
  maxWidth: '50dvw',
  // PHONE: the desktop rail steps aside entirely (same gate as ChatSidebar);
  // the same body re-presents in the shared off-canvas sheet
  // (MailMobileRail, state `_mailRailOpen`).
  '@mobileL': { display: 'none' },

  Middle: {
    flex: '1',
    overflow: 'auto',
    MailSidebarBody: {}
  }
}
